// A small CSV reader for call lists exported from Google Sheets or Excel:
// quoted fields, doubled quotes, commas or semicolons, CRLF, a BOM.

export function parseCsv(text: string): string[][] {
  const src = text.replace(/^\uFEFF/, '');
  const first = src.split(/\r?\n/, 1)[0] ?? '';
  const sep = (first.match(/;/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') {
        quoted = false;
      } else {
        cell += c;
      }
    } else if (c === '"') {
      quoted = true;
    } else if (c === sep) {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      cell = '';
      rows.push(row);
      row = [];
    } else {
      cell += c;
    }
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((x) => x.trim() !== ''));
}

export const CALL_FIELDS = [
  { id: 'clinic', label: 'Practice name', test: /practice|clinic|klinik|name|nazov|názov/i },
  { id: 'locality', label: 'Locality or city', test: /locality|city|location|şehir|mesto|lokal/i },
  { id: 'phone', label: 'Phone', test: /phone|tel|mobile|numb|telefon/i },
  { id: 'email', label: 'Email', test: /mail/i },
  { id: 'website', label: 'Website', test: /web|site|url/i },
  { id: 'fb_ads', label: 'Runs Facebook ads', test: /facebook|\bfb\b|meta/i },
  { id: 'google_ads', label: 'Runs Google ads', test: /google/i },
  { id: 'status', label: 'Call status so far', test: /^call$|status|outcome|result/i },
  { id: 'note', label: 'Note', test: /note|poznám|comment/i },
] as const;

export type CallFieldId = (typeof CALL_FIELDS)[number]['id'];

/** For each field, the index of the header that looks like it, or -1. */
export function guessColumns(headers: string[]): Record<CallFieldId, number> {
  const out = {} as Record<CallFieldId, number>;
  const used = new Set<number>();
  // Specific fields first, so "Active Google Ads" is not taken for a name.
  const order: CallFieldId[] = [
    'fb_ads',
    'google_ads',
    'email',
    'phone',
    'website',
    'status',
    'note',
    'locality',
    'clinic',
  ];
  for (const id of order) {
    const f = CALL_FIELDS.find((x) => x.id === id)!;
    const i = headers.findIndex((h, idx) => !used.has(idx) && f.test.test(h.trim()));
    out[id] = i;
    if (i >= 0) used.add(i);
  }
  return out;
}

const JUNK_EMAIL = /@sentry|wixpress\.com|\.(png|jpe?g|gif|webp)$|^(example|test)@/i;

export function toCallRows(
  rows: string[][],
  map: Record<CallFieldId, number>,
): Record<string, string>[] {
  return rows.slice(1).map((r) => {
    const o: Record<string, string> = {};
    for (const f of CALL_FIELDS) {
      const i = map[f.id];
      if (i >= 0) o[f.id] = (r[i] ?? '').trim();
    }
    // Scraped addresses of site builders and error trackers are not the clinic's.
    if (o.email && JUNK_EMAIL.test(o.email)) o.email = '';
    return o;
  });
}
